'use client';

import { useState } from 'react';
import ProcessTimeline from './ProcessTimeline';
import ProcessPhase from './ProcessPhase';
import ProcessComparison from './ProcessComparison';
import VirtualConsultation from './VirtualConsultation';
import ProjectDashboard from './ProjectDashboard';
import ClientTestimonials from '@/app/homepage/components/ClientTestimonials';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface Phase {
  number: string;
  title: string;
  duration: string;
  description: string;
  deliverables: string[];
  clientResponsibilities: string[];
  qualityCheckpoints: string[];
}

interface ComparisonItem {
  feature: string;
  hayatInteriors: string;
  typicalIndustry: string;
  advantage: boolean;
}

interface VirtualFeature {
  icon: string;
  title: string;
  description: string;
}

interface DashboardFeature {
  icon: string;
  title: string;
  description: string;
  image: string;
  alt: string;
}

const DesignProcessInteractive = () => {
  const [activePhase, setActivePhase] = useState(0);

  const phases: Phase[] = [ 
  {
    number: "01",
    title: "Discovery & Consultation",
    duration: "1-2 weeks",
    description: "We begin by understanding your lifestyle, aesthetic preferences, cultural values, and functional needs. This phase includes site visits, family interviews, and lifestyle analysis to create a comprehensive design brief that honors both modern living and Indian heritage.",
    deliverables: [
    "Detailed project brief and scope document",
    "Site analysis",
    "Budget framework and timeline proposal",
    "Mood boards reflecting your style preferences"],

    clientResponsibilities: [
    "Share inspiration images and design preferences",
    "Provide access to property for measurements",
    "Discuss budget expectations openly",
    "Share family lifestyle and cultural requirements"],

    qualityCheckpoints: [
    "Comprehensive understanding of client vision",
    "Accurate site documentation",
    "Realistic budget and timeline alignment",
    "Cultural and functional requirements captured"]

  },
  {
    number: "02",
    title: "Design Concept Development",
    duration: "2-3 weeks",
    description: "Our design team creates multiple concept options that blend contemporary aesthetics with timeless Indian craftsmanship. We develop detailed 3D visualizations, material palettes, and spatial layouts that bring your vision to life while ensuring cultural authenticity.",
    deliverables: [
    "3D rendered visualizations of key spaces",
    "Detailed floor plans and furniture layouts",
    "Material and color palette presentations",
    "Preliminary cost estimates for each concept"],

    clientResponsibilities: [
    "Review and provide feedback on concepts",
    "Select preferred design direction",
    "Approve material and color selections",
    "Confirm budget alignment"],

    qualityCheckpoints: [
    "Design concepts align with brief",
    "3D visualizations accurately represent spaces",
    "Material selections meet quality standards",
    "Traditional principles integrated where requested"]

  },
  {
    number: "03",
    title: "Design Refinement",
    duration: "2-3 weeks",
    description: "We take the approved concept and develop it further with clear and detailed technical drawings. We design custom furniture and clearly specify all materials to be used. This stage also involves working closely with local artisans and craftspeople to make sure traditional elements are created authentically and blended properly with modern design structures.",
    deliverables: [
    "Detailed working drawings and specifications",
    "Custom furniture design drawings",
    "Lighting and electrical layout plans",
    "Final material specifications and samples"],

    clientResponsibilities: [
    "Review and approve detailed drawings",
    "Make final material and finish selections",
    "Approve custom furniture designs",
    "Sign off on technical specifications"],

    qualityCheckpoints: [
    "All technical details accurately documented",
    "Custom designs meet functional requirements",
    "Material specifications match approved samples",
    "Coordination with vendors confirmed"]

  },
  {
    number: "04",
    title: "Procurement & Fabrication",
    duration: "3-6 weeks",
    description: "We source premium materials, coordinate with trusted local artisans, and oversee custom fabrication of furniture and fixtures. Our team ensures quality control at every stage, from material selection to final finishing, maintaining our commitment to sustainable and locally-sourced elements.",
    deliverables: [
    "Procurement of all materials and furnishings",
    "Custom furniture and fixture fabrication",
    "Quality inspection reports",
    "Delivery schedule and logistics plan"],

    clientResponsibilities: [
    "Approve material samples upon delivery",
    "Make timely payment as per schedule",
    "Coordinate site access for deliveries",
    "Review and approve fabricated items"],

    qualityCheckpoints: [
    "All materials meet specified quality standards",
    "Custom fabrication matches approved designs",
    "Delivery timelines maintained",
    "Artisan work meets craftsmanship standards"]

  },
  {
    number: "05",
    title: "Installation & Execution",
    duration: "3-6 weeks",
    description: "Our experienced project team manages on-site execution with meticulous attention to detail. We coordinate all trades, ensure compliance with Indian building standards, and maintain daily communication with you through our project management dashboard for complete transparency.",
    deliverables: [
    "Complete installation of all elements",
    "Daily progress updates via dashboard",
    "Quality assurance inspections",
    "Snag list resolution and final touches"],

    clientResponsibilities: [
    "Ensure site access for installation team",
    "Review daily progress updates",
    "Approve completed work at milestones",
    "Coordinate with building management if needed"],

    qualityCheckpoints: [
    "Installation matches approved drawings",
    "All safety and building codes followed",
    "Quality standards maintained throughout",
    "Client feedback incorporated promptly"]

  },
  {
    number: "06",
    title: "Styling & Handover",
    duration: "1-2 weeks",
    description: "We complete your home with carefully curated styling elements, artwork, and accessories that reflect your personality and cultural heritage. Final walkthrough ensures every detail is perfect, followed by comprehensive handover documentation and ongoing support.",
    deliverables: [
    "Complete styling with accessories and artwork",
    "Professional photography of finished spaces",
    "Comprehensive handover documentation",
    "Maintenance guides and warranty information"],

    clientResponsibilities: [
    "Final walkthrough and approval",
    "Sign off on completion certificate",
    "Provide feedback for testimonial",
    "Refer friends and family"],

    qualityCheckpoints: [
    "All styling elements enhance overall design",
    "Photography captures design intent",
    "Documentation complete and organized",
    "Client satisfaction confirmed"]

  }];




  const comparisons: ComparisonItem[] = [
  {
    feature: "Initial Consultation",
    hayatInteriors: "Comprehensive lifestyle analysis with cultural sensitivity and family interviews",
    typicalIndustry: "Basic requirement gathering and site visit",
    advantage: true
  },
  {
    feature: "Design Visualization",
    hayatInteriors: "Photorealistic 3D renders with multiple concept options and virtual walkthroughs",
    typicalIndustry: "2D drawings with limited visualization",
    advantage: true
  },
  {
    feature: "Material Sourcing",
    hayatInteriors: "Curated selection from local artisans and sustainable sources with quality guarantees",
    typicalIndustry: "Standard vendor catalog selections",
    advantage: true
  },
  {
    feature: "Project Tracking",
    hayatInteriors: "Real-time dashboard with daily updates, photos, and milestone tracking",
    typicalIndustry: "Weekly phone updates or site visits",
    advantage: true
  },
  {
    feature: "Quality Control",
    hayatInteriors: "Multi-stage inspections at procurement, fabrication, and installation phases",
    typicalIndustry: "Final inspection only",
    advantage: true
  },
  {
    feature: "Cultural Integration",
    hayatInteriors: "Traditional design integration, traditional craftsmanship, and Indian design heritage incorporation",
    typicalIndustry: "Generic modern design approach",
    advantage: true
  },
  {
    feature: "Post-Completion Support",
    hayatInteriors: "12-months Service warranty with dedicated support team and maintenance guidance",
    typicalIndustry: "Limited or no post-completion support",
    advantage: true
  },
  {
    feature: "Remote Collaboration",
    hayatInteriors: "Virtual consultations, digital approvals, and video progress updates for NRI clients",
    typicalIndustry: "In-person meetings required",
    advantage: true
  }];


  const virtualFeatures: VirtualFeature[] = [
  {
    icon: "VideoCameraIcon",
    title: "Zoom Meeting",
    description: "Face-to-face consultations with designers via Zoom"
  },
  {
    icon: "DocumentTextIcon",
    title: "Digital Approvals",
    description: "Review and approve designs, materials, and progress through secure online portal"
  },
  {
    icon: "CameraIcon",
    title: "Live Site Updates",
    description: "Real-time photos and videos of installation progress sent directly to your dashboard"
  },
  {
    icon: "ChatBubbleLeftRightIcon",
    title: "Instant Messaging",
    description: "Direct communication channel with project manager for quick queries and updates"
  }];


  const dashboardFeatures: DashboardFeature[] = [
  {
    icon: "ChartBarIcon",
    title: "Progress Tracking",
    description: "Visual timeline showing completed milestones, current phase status, and upcoming activities. Track budget utilization and timeline adherence in real-time with detailed breakdowns.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_116b17f40-1764672098874.png",
    alt: "Modern dashboard interface showing project progress charts and timeline on computer screen"
  },
  {
    icon: "PhotoIcon",
    title: "Photo Gallery",
    description: "Organized collection of daily site photos, material samples, and fabrication progress. Compare before-and-after images and access high-resolution documentation of every project phase.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e94ce8a9-1764652054967.png",
    alt: "Interior design photo gallery showing multiple room transformations in grid layout"
  },
  {
    icon: "DocumentCheckIcon",
    title: "Document Center",
    description: "Centralized repository for all project documents including contracts, drawings, specifications, invoices, and warranties. Download and review anytime from anywhere.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14d98b976-1765159450413.png",
    alt: "Digital document management system showing organized project files and folders"
  },
  {
    icon: "ClipboardDocumentCheckIcon",
    title: "Approval Workflow",
    description: "Streamlined approval process for designs, materials, and changes. Receive notifications for pending approvals and track decision history with timestamp documentation.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_190d9bae9-1764660646211.png",
    alt: "Workflow approval interface showing checklist and approval status indicators"
  }];


  return (
    <div className="space-y-16">
      <section>
        <ProcessTimeline
          phases={phases}
          activePhase={activePhase}
          onPhaseSelect={setActivePhase} />

      </section>

      <section>
        <ProcessPhase
          phase={phases[activePhase]}
          isActive={true}
          onPhaseClick={() => {}} />

      </section>

      <ClientTestimonials />

      <section>
        <VirtualConsultation features={virtualFeatures} />
      </section>

      <section>
        <ProjectDashboard features={dashboardFeatures} />
      </section>

      <section>
        <div className="text-center mb-8">
          <h2 className="font-headline text-3xl font-bold text-foreground mb-4">
            How We Compare To Industry Standards
          </h2>
          <p className="font-body text-muted-foreground max-w-2xl mx-auto">
            See how our process delivers superior value and transparency compared to typical interior design practices
          </p>
        </div>

        <ProcessComparison comparisons={comparisons} />
      </section>

      <section className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-lg p-8 lg:p-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-6">
            <Icon name="ShieldCheckIcon" size={32} className="text-primary-foreground" />
          </div>
          <h2 className="font-headline text-3xl font-bold text-foreground mb-4">
            Our Commitment To You
          </h2>
          <p className="font-body text-foreground leading-relaxed mb-8">
            Every Hayat Interiors project comes with a comprehensive 12-month warranty covering all workmanship and materials. We provide detailed maintenance guides tailored to Indian climate conditions, dedicated post-completion support team, and priority service for any adjustments needed. Your satisfaction and peace of mind are our ultimate deliverables.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-card rounded-lg p-6">
              <Icon name="ClockIcon" size={32} className="text-primary mx-auto mb-3" />
              <h3 className="font-body font-semibold text-foreground mb-2">10-Years Warranty</h3>
              <p className="font-body text-sm text-muted-foreground">
                Comprehensive coverage on all modular work
              </p>
            </div>
            <div className="bg-card rounded-lg p-6">
              <Icon name="UserGroupIcon" size={32} className="text-primary mx-auto mb-3" />
              <h3 className="font-body font-semibold text-foreground mb-2">Dedicated Support</h3>
              <p className="font-body text-sm text-muted-foreground">
                Personal support team for post-completion assistance
              </p>
            </div>
            <div className="bg-card rounded-lg p-6">
              <Icon name="DocumentTextIcon" size={32} className="text-primary mx-auto mb-3" />
              <h3 className="font-body font-semibold text-foreground mb-2">Maintenance Guides</h3>
              <p className="font-body text-sm text-muted-foreground">
                Detailed care instructions for Indian climate
              </p>
            </div>
          </div>
          <Link
            href="/contact"
            className="inline-flex items-center space-x-2 px-8 py-4 bg-primary text-primary-foreground font-body font-semibold rounded-md hover:bg-primary/90 transition-all duration-200 shadow-strong">

            <span>Start Your Design Journey</span>
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </section>
    </div>);

};

export default DesignProcessInteractive;